import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PencilSimple, Trash } from "@phosphor-icons/react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function ClassSubjects({ classId, teachers }) {
  const [rows, setRows] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({ subject_id: "", teacher_id: "", periods_per_week: "3" });
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState("");
  const [editForm, setEditForm] = useState({ teacher_id: "", periods_per_week: "" });

  const load = useCallback(() => {
    api
      .get(`/school/classes/${classId}/subjects`)
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, [classId]);

  useEffect(() => {
    setEditId("");
    setForm({ subject_id: "", teacher_id: "", periods_per_week: "3" });
    load();

    api
      .get("/school/subjects")
      .then((r) => setSubjects(r.data))
      .catch((e) => toast.error(formatErr(e)));
  }, [classId, load]);

  const availableSubjects = subjects.filter((s) => !rows.some((r) => r.subject_id === s.id));

  const add = async () => {
    if (!form.subject_id || !form.teacher_id || !Number(form.periods_per_week)) {
      toast.error("Choose a subject, teacher and periods/week");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/school/classes/${classId}/subjects`, {
        ...form,
        periods_per_week: Number(form.periods_per_week),
      });
      toast.success("Subject assigned");
      setForm({ subject_id: "", teacher_id: "", periods_per_week: "3" });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (r) => {
    setEditId(r.id);
    setEditForm({
      teacher_id: r.teacher_id,
      periods_per_week: String(r.periods_per_week),
    });
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/school/class-subjects/${editId}`, {
        teacher_id: editForm.teacher_id,
        periods_per_week: Number(editForm.periods_per_week),
      });
      toast.success("Assignment updated");
      setEditId("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/class-subjects/${id}`);
      toast.success("Assignment removed — timetable slots are unchanged");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div data-testid="class-subjects-section" className="border-t border-slate-100 pt-4 mt-1">
      <p className="font-heading font-bold text-sm">Subjects &amp; Teachers</p>
      <p className="text-[11px] text-slate-400 mt-0.5 mb-3">
        Official subject-teacher assignments for this class. Existing timetable slots are not affected.
      </p>

      {rows.length > 0 && (
        <div className="space-y-2 mb-3">
          <div className="hidden sm:grid grid-cols-[1fr_1fr_70px_64px] gap-2 text-[10px] uppercase tracking-wide text-slate-400 font-semibold px-1">
            <span>Subject</span>
            <span>Assigned teacher</span>
            <span>Periods/wk</span>
            <span />
          </div>

          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`class-subject-row-${r.id}`}
              className="bg-[#F4F5F7] rounded-xl px-3 py-2.5"
            >
              {editId === r.id ? (
                <div className="grid sm:grid-cols-[1fr_1fr_70px_64px] gap-2 items-center">
                  <span className="text-sm font-semibold">{r.subject_name}</span>

                  <Select
                    value={editForm.teacher_id}
                    onValueChange={(v) => setEditForm({ ...editForm, teacher_id: v })}
                  >
                    <SelectTrigger
                      data-testid={`cs-edit-teacher-${r.id}`}
                      className="bg-white h-9"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    type="number"
                    min="1"
                    max="40"
                    data-testid={`cs-edit-periods-${r.id}`}
                    value={editForm.periods_per_week}
                    onChange={(e) =>
                      setEditForm({ ...editForm, periods_per_week: e.target.value })
                    }
                    className="bg-white h-9"
                  />

                  <span className="flex gap-1 justify-end items-center">
                    <Button
                      size="sm"
                      data-testid={`cs-edit-save-${r.id}`}
                      onClick={saveEdit}
                      className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold h-8 px-3"
                    >
                      Save
                    </Button>
                    <button
                      data-testid={`cs-edit-cancel-${r.id}`}
                      onClick={() => setEditId("")}
                      className="text-xs font-semibold text-slate-400 hover:text-slate-600 px-1"
                    >
                      Cancel
                    </button>
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_1fr_70px_64px] gap-2 items-center">
                  <span className="text-sm font-semibold">{r.subject_name}</span>

                  <span className="flex gap-1 justify-end col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto">
                    <button
                      data-testid={`cs-edit-${r.id}`}
                      onClick={() => startEdit(r)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200"
                      title="Edit assignment"
                    >
                      <PencilSimple size={14} />
                    </button>
                    <button
                      data-testid={`cs-delete-${r.id}`}
                      onClick={() => remove(r.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200"
                      title="Remove assignment"
                    >
                      <Trash size={14} />
                    </button>
                  </span>

                  <span className="text-sm text-slate-600">{r.teacher_name}</span>
                  <span className="text-sm font-bold">{r.periods_per_week}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 && (
        <p
          className="text-xs text-slate-400 mb-3"
          data-testid="class-subjects-empty"
        >
          No subject assignments yet.
        </p>
      )}

      <div
        className="grid sm:grid-cols-[1fr_1fr_70px_auto] gap-2 items-center"
        data-testid="class-subject-add-row"
      >
        <Select
          value={form.subject_id}
          onValueChange={(v) => setForm({ ...form, subject_id: v })}
        >
          <SelectTrigger data-testid="cs-add-subject" className="bg-white">
            <SelectValue placeholder="Subject" />
          </SelectTrigger>
          <SelectContent>
            {availableSubjects.map((s) => (
              <SelectItem
                key={s.id}
                value={s.id}
                data-testid={`cs-subject-option-${s.id}`}
              >
                {s.subject_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={form.teacher_id}
          onValueChange={(v) => setForm({ ...form, teacher_id: v })}
        >
          <SelectTrigger data-testid="cs-add-teacher" className="bg-white">
            <SelectValue placeholder="Teacher" />
          </SelectTrigger>
          <SelectContent>
            {teachers.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          type="number"
          min="1"
          max="40"
          data-testid="cs-add-periods"
          value={form.periods_per_week}
          onChange={(e) =>
            setForm({ ...form, periods_per_week: e.target.value })
          }
          className="bg-white"
        />

        <Button
          data-testid="cs-add-btn"
          onClick={add}
          disabled={saving}
          className="rounded-full bg-[#00194E] hover:bg-[#00194E]/90 text-white font-bold"
        >
          {saving ? "Adding…" : "+ Add Subject"}
        </Button>
      </div>

      {subjects.length === 0 && (
        <p
          className="text-[11px] text-amber-700 mt-2"
          data-testid="class-subjects-no-subjects"
        >
          Add subjects under Timetable → Rooms &amp; subjects first.
        </p>
      )}
    </div>
  );
}
